import React, { useState } from "react";
import PropTypes from "prop-types";
// eslint-disable-next-line import/no-cycle
import { Button } from "../Button";
// eslint-disable-next-line import/no-cycle
import { Link } from "../../sharedComponents/Link";
// eslint-disable-next-line import/no-cycle
import { AlertConnector as Alert } from "../Alert";
import { TextInput } from "../FormElements/Input/Input";
import { Dropdown } from "../FormElements/Select/Select";
import gatewaysData from "../Header/Gateway/Menu/HeaderGateways.mock.json";
import * as styles from "./SignUpForm.module.scss";

const defaultLabels = {
	title: "Subscribe to our newsletter",
	subtitle:
		"Sign up for access to exclusive Sunwing deals, promotional offers and vacation inspiration",
	email: "Email",
	gateway: "Gateway",
	submit: "Sign-up",
	unsubscribe: "You may unsubscribe at any time.",
	successMessage: "You have successfully signed up to become a Sunwing member.",
	errorMessage: "Your email could not be confirmed. Please try again.",
	emailValidation: "Please enter a valid email",
	gatewayValidation: "Please select a gateway",
};

const SignUpForm = ({ id, lang, labels: providedLabels, links }) => {
	const [formEmail, setFormEmail] = useState("");
	const [formGateway, setFormGateway] = useState("");
	const [emailError, setEmailError] = useState("");
	const [isValid, setIsValid] = useState(true);
	const [isSuccess, setIsSuccess] = useState(false);
	const [isError, setIsError] = useState();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [gatewayError, setGatewayError] = useState("");
	const [gatewayIsValid, setGatewayIsValid] = useState(false);
	const api = process.env.GATSBY_SWG_CUSTOMER_API;
	const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

	const labels = {
		...defaultLabels,
		...providedLabels,
	};

	const gatewayModData = gatewaysData.map(menu =>
		menu.gateways.map(gateway => ({
			value: gateway.value,
			label: `${gateway.label} (${gateway.value})`,
		}))
	);

	const handleSubmit = e => {
		e.preventDefault();
		setIsError();
		setIsSuccess(false);
		if (formGateway === "") {
			setGatewayError(labels.gatewayValidation);
			setGatewayIsValid(true);
		}
		if (!emailRegex.test(formEmail)) {
			setIsValid(false);
			setEmailError(labels.emailValidation);
		}
		if (emailRegex.test(formEmail) && formGateway) {
			setIsSubmitting(true);
			setIsValid(true);
			setEmailError("");
			const payload = {
				firstName: "",
				lastName: "",
				brandCode: "SWG",
				emailAddress: {
					email: formEmail,
					isSubscribed: true,
				},
				contactPreferences: {
					languageCode: lang === "fr" ? "fr" : "en",
					departureCities: [
						{
							code: formGateway,
						},
					],
				},
				context: {
					campaign: {
						code: "sunwing-re-design",
						source: "web.dsl.scn",
						channel: "ONLINE",
						type: "SUBSCRIPTION",
					},
					page: {
						url: window.location.href,
						title: document.title,
					},
				},
			};

			fetch(api, {
				method: "POST",
				body: JSON.stringify(payload),
				headers: {
					"Content-type": "application/json",
				},
			})
				.then(res => res.json())
				.then(() => {
					setIsSuccess(labels.successMessage);
					setIsSubmitting(false);
					if (!window.dataLayer) {
						window.dataLayer = [];
					}
					window.dataLayer.push({
						event: "email_signup",
						email_gateway: formGateway,
						email_language: lang,
						email_value: formEmail,
					});
				})
				.catch(err => {
					console.error("Unable to submit the form", err);
					setIsError(labels.errorMessage);
					setIsSubmitting(false);
				});
		}
	};

	const handleChange = e => {
		setGatewayIsValid(false);
		setIsSuccess(false);
		setIsError("");
		setIsValid(false);
		setEmailError("");
		if (e.target.type === "email") {
			setFormEmail(e.target.value);
		} else {
			setFormGateway(e.target.value);
		}
	};
	return (
		<div className={styles.formContainer}>
			{isSuccess && (
				<Alert variant="success" className={styles.signUpFormAlert}>
					{isSuccess}
				</Alert>
			)}
			{isError && <Alert className={styles.signUpFormAlert}>{isError}</Alert>}
			<div className={styles.formCta}>
				<span className={styles.formCtaTitle}>{labels.title}</span>
				<span className={styles.formCtaSubtitle}>{labels.subtitle}</span>
			</div>
			<div className={styles.formContainerSignUp}>
				<form onSubmit={handleSubmit} className={styles.formForm}>
					<div className={styles.emailContainer}>
						<TextInput
							label={labels.email}
							placeholder={labels.email}
							type="email"
							value={formEmail}
							onChange={handleChange}
							error={!isValid}
							errorMsg={emailError}
							labelHidden
							full
							id={`email-${id}`}
						/>
					</div>
					<div className={styles.formSelectContainer}>
						<Dropdown
							label={labels.gateway}
							placeholder={{ label: labels.gateway, value: "" }}
							items={gatewayModData.flat()}
							onChange={handleChange}
							error={gatewayIsValid}
							errorMsg={gatewayError}
							value={formGateway}
							labelHidden
							full
							name={`gateway-${id}`}
							id={`gateway-${id}`}
						/>
					</div>
					<div className={styles.formButton}>
						<Button full disabled={isSubmitting} type="submit" onClick={handleSubmit}>
							{labels.submit}
						</Button>
					</div>
				</form>
				<div className={styles.formRight}>
					<div>{labels.unsubscribe}</div>
					{links &&
						links.map(link => (
							<Link key={link.url} to={link.url} className={styles.formRightLink}>
								{link.text}
							</Link>
						))}
				</div>
			</div>
		</div>
	);
};

SignUpForm.propTypes = {
	id: PropTypes.string.isRequired,
	lang: PropTypes.oneOf(["en", "fr"]),
	labels: PropTypes.shape({
		title: PropTypes.string,
		subtitle: PropTypes.string,
		email: PropTypes.string,
		gateway: PropTypes.string,
		submit: PropTypes.string,
		unsubscribe: PropTypes.string,
		errorMessage: PropTypes.string,
		successMessage: PropTypes.string,
		gatewayValidation: PropTypes.string,
		emailValidation: PropTypes.string,
	}),
	links: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string,
			url: PropTypes.string,
		})
	),
};

SignUpForm.defaultProps = {
	lang: "en",
	labels: defaultLabels,
	links: undefined,
};

export default SignUpForm;
export { SignUpForm };
